import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Spin } from "antd";
import DashboardFilterBar from "./components/DashboardFilterBar";
import KPICard from "../../components/common/KPICard";
import ChartShell from "./components/ChartShell";
import TrendChart from "./components/TrendChart";
import PartnerDetailsPanel from "./components/PartnerDetailsPanel";
import {
  OVERALL_KPI_DEFS,
  ORDERS_TREND_SERIES,
  FINANCIAL_TREND_SERIES,
  ALL_VALUE,
  defaultOverallFilters,
} from "./constants";
import { fetchPartnerStatusStats, mapPerformerRecord } from "./dashboardData";
import {
  getDashboardOverall,
  getDashboardOnboarding,
} from "../../services/dashboard.service";

const EMPTY_OVERALL = {
  kpis: {
    ordersRider: null,
    totalOrders: null,
    collectedAmount: null,
    collectedRiderAmount: null,
    balance: null,
  },
  ordersTrend: [],
  financialTrend: [],
  topPerformers: [],
};

/** The Overall view is defined for exactly one year + one month. */
function hasPeriod(filters) {
  return Boolean(filters.year && filters.month);
}

/** Strip the "All" sentinel and comma-join zones for the API. */
function toQueryParams(filters) {
  const params = { year: filters.year, month: filters.month };
  if (filters.project && filters.project !== ALL_VALUE) params.project = filters.project;
  if (filters.fleetType && filters.fleetType !== ALL_VALUE) params.fleetType = filters.fleetType;
  if (filters.city && filters.city !== ALL_VALUE) params.city = filters.city;
  if (filters.zones?.length) params.zones = filters.zones.join(",");
  return params;
}

const formatAmount = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const formatCount = (value) => Number(value || 0).toLocaleString();

export default function OverallTab() {
  const [filters, setFilters] = useState(defaultOverallFilters);
  const [overall, setOverall] = useState(EMPTY_OVERALL);
  const [overallLoading, setOverallLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    activePercent: null,
  });
  const [onboarding, setOnboarding] = useState({
    loading: false,
    totalActive: 0,
    documents: [],
  });

  // Filter changes fire overlapping requests; without a sequence guard a slow
  // earlier response can land after a newer one and show data for stale filters.
  const overallRequest = useRef(0);
  const statsRequest = useRef(0);

  const loadOverall = useCallback(async (activeFilters) => {
    if (!hasPeriod(activeFilters)) {
      overallRequest.current += 1;
      setOverall(EMPTY_OVERALL);
      setOverallLoading(false);
      return;
    }

    const requestId = (overallRequest.current += 1);
    setOverallLoading(true);
    try {
      const data = await getDashboardOverall(toQueryParams(activeFilters));
      if (requestId !== overallRequest.current) return;
      setOverall({
        kpis: data?.kpis || EMPTY_OVERALL.kpis,
        ordersTrend: data?.ordersTrend || [],
        financialTrend: data?.financialTrend || [],
        topPerformers: (data?.topPerformers || []).map(mapPerformerRecord),
      });
    } catch {
      if (requestId !== overallRequest.current) return;
      setOverall(EMPTY_OVERALL);
      message.error("Failed to load dashboard data");
    } finally {
      if (requestId === overallRequest.current) setOverallLoading(false);
    }
  }, []);

  const loadPartnerStats = useCallback(async (activeFilters) => {
    const requestId = (statsRequest.current += 1);
    setStatsLoading(true);
    try {
      const next = await fetchPartnerStatusStats(activeFilters);
      if (requestId !== statsRequest.current) return;
      setStats(next);
    } catch {
      if (requestId !== statsRequest.current) return;
      message.error("Failed to load partner status");
    } finally {
      if (requestId === statsRequest.current) setStatsLoading(false);
    }
  }, []);

  // Every one of the six filters feeds the KPIs, trends and Top Performers.
  useEffect(() => {
    loadOverall(filters);
  }, [filters, loadOverall]);

  // Status counts are the one piece left as-is: GET /riders only supports project + zone.
  const statsFilters = useMemo(
    () => ({ project: filters.project, zones: filters.zones }),
    [filters.project, filters.zones]
  );

  useEffect(() => {
    loadPartnerStats(statsFilters);
  }, [statsFilters, loadPartnerStats]);

  // Onboarding ignores the filter bar by design — fetched once, never refetched on filter change.
  useEffect(() => {
    let cancelled = false;
    setOnboarding((prev) => ({ ...prev, loading: true }));

    getDashboardOnboarding()
      .then((data) => {
        if (cancelled) return;
        setOnboarding({
          loading: false,
          totalActive: data?.totalActive || 0,
          documents: data?.documents || [],
        });
      })
      .catch(() => {
        if (!cancelled) setOnboarding({ loading: false, totalActive: 0, documents: [] });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRefresh = () => {
    setFilters(defaultOverallFilters());
  };

  const periodSelected = hasPeriod(filters);
  const days = overall.ordersTrend.map((row) => row.day);
  const noPeriodText = "Select 1 Year and 1 Month to Display View";

  return (
    <div className="dash-tab">
      <DashboardFilterBar
        values={filters}
        onChange={setFilters}
        onRefresh={handleRefresh}
        showYearMonth
        showAllOption
        multiZone
      />

      <div className="dash-body">
        {!periodSelected && (
          <div className="dash-period-notice">{noPeriodText}</div>
        )}

        <Spin spinning={overallLoading}>
          <div className="dash-kpi-grid cols-6">
            {OVERALL_KPI_DEFS.map((kpi) => (
              <KPICard
                key={kpi.key}
                label={kpi.label}
                value={
                  kpi.placeholder || !periodSelected
                    ? null
                    : overall.kpis?.[kpi.key]
                }
                unit={kpi.unit}
                allowNegative={kpi.allowNegative}
              />
            ))}
          </div>

          <div className="dash-overall-bottom">
            <ChartShell
              title="Total Orders Trend"
              legend={ORDERS_TREND_SERIES}
              emptyText={periodSelected ? "No data available" : noPeriodText}
            >
              {periodSelected && days.length > 0 ? (
                <TrendChart
                  days={days}
                  data={overall.ordersTrend}
                  series={ORDERS_TREND_SERIES}
                  formatValue={formatCount}
                />
              ) : null}
            </ChartShell>

            <ChartShell
              title="Financial Comparison"
              legend={FINANCIAL_TREND_SERIES}
              emptyText={periodSelected ? "No data available" : noPeriodText}
            >
              {periodSelected && overall.financialTrend.length > 0 ? (
                <TrendChart
                  days={overall.financialTrend.map((row) => row.day)}
                  data={overall.financialTrend}
                  series={FINANCIAL_TREND_SERIES}
                  formatValue={formatAmount}
                />
              ) : null}
            </ChartShell>

            <PartnerDetailsPanel
              loading={statsLoading}
              stats={stats}
              performers={overall.topPerformers}
              performersLoading={overallLoading}
              onboarding={onboarding}
            />
          </div>
        </Spin>
      </div>
    </div>
  );
}
